import type { DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import type { Buoc6PhuongDien } from '@/types/performance/buoc6phuongdien';
import { ACTION_PLAN_V2 } from '@/components/common/ActionPlanV2/constants';
import type { TargetSelectPerspectiveGroup } from '@/components/common/ActionPlan/utils/du-an-utils';
import { actionPlanV2Api } from '@/services/action-plan-v2';
import { buoc6Api } from '@/services/performance/target-api';
import type { RequestSDK } from '@/types/sdk/requestSDK';

// #region displayNode

/** Node mục tiêu chỉ hiển thị (`apv2-mt-*`) — leaf, không import module V2 khác. */

/** Prefix id node mục tiêu chỉ hiển thị trên bảng V2. */
export const MUC_TIEU_DISPLAY_ID_PREFIX = 'apv2-mt-';

export function isDisplayMucTieuRecord(
  record?: { congViec?: unknown } | null
): boolean {
  if (!record?.congViec) return false;
  const cv = record.congViec as { id?: string; isDisplayMucTieu?: boolean };
  if (cv.isDisplayMucTieu === true) return true;
  const id = String(cv.id ?? '');
  return id.startsWith(MUC_TIEU_DISPLAY_ID_PREFIX);
}

/**
 * Bảng «Không nhóm»: bỏ nút mục tiêu ảo ở gốc, con của chúng thành gốc (giữ thứ tự mục tiêu rồi thứ tự trong mục tiêu).
 * Gốc không phải nút mục tiêu giữ nguyên → gọi lại trên dữ liệu đã bỏ (nháp bảng đẩy về) không đổi gì.
 */
export function boNutMucTieu<T extends object>(roots: T[]): T[] {
  const laNutMucTieu = (r: T) => isDisplayMucTieuRecord(r as Parameters<typeof isDisplayMucTieuRecord>[0]);
  if (!roots.some(laNutMucTieu)) return roots;
  return roots.flatMap((r) => (laNutMucTieu(r) ? (((r as { childs?: T[] | null }).childs ?? []) as T[]) : [r]));
}

/** ParentID gốc cho công việc tạo mới dưới mục tiêu (Action Plan V2). */
export const APV2_ROOT_TASK_PARENT_ID = '';

/** ParentID rỗng = công việc gốc trên UI. Node mục tiêu (apv2-mt-*) không phải cha persist. */
export function isApv2RootTaskParentId(parentId: string | undefined | null): boolean {
  const p = String(parentId ?? '').trim();
  if (!p) return true;
  return p.toLowerCase().startsWith(MUC_TIEU_DISPLAY_ID_PREFIX);
}

// #endregion displayNode

// #region filterData

/** Danh sách mục tiêu + dữ liệu TargetSelect từ actionplanv2/loadmuctieu — leaf, không import module V2 khác. */

export type MucTieuListItem = {
  id: string;
  name: string;
  taskCount?: number;
};

export type MucTieuFilterData = {
  listTableBSCNEW: Buoc6PhuongDien[];
  listMucTieuPhuTrachIDs: string[];
  listDuAn: DuAnCongViecTree[];
};

function parseMucTieuNameAndCount(raw: string): { name: string; taskCount: number } {
  const text = String(raw ?? '').trim();
  if (!text) return { name: '—', taskCount: 0 };
  const match = text.match(/^(.*?)\s*\((\d+)\)\s*$/);
  if (!match) return { name: text, taskCount: 0 };
  const name = (match[1] || '').trim() || text;
  return { name, taskCount: Number(match[2]) || 0 };
}

function normalizeLoadMucTieuItem(
  raw: Record<string, unknown>
): { id: string; name: string; taskCount: number; rawName: string } | null {
  const id = String(raw.id ?? raw.ID ?? '').trim();
  if (!id) return null;
  const rawName = String(raw.ten ?? raw.Ten ?? raw.text ?? raw.Text ?? id).trim() || id;
  const { name, taskCount: parsedCount } = parseMucTieuNameAndCount(rawName);
  const soCongViecRaw = raw.soCongViec ?? raw.SoCongViec;
  const soCongViec =
    typeof soCongViecRaw === 'number'
      ? soCongViecRaw
      : Number(soCongViecRaw);
  const taskCount =
    Number.isFinite(soCongViec) && soCongViec > 0 ? Math.floor(soCongViec) : parsedCount;
  return { id, name: name || id, taskCount, rawName };
}

export function mapLoadMucTieuV2Response(data: Record<string, unknown>): {
  items: MucTieuListItem[];
  filterData: MucTieuFilterData | null;
} {
  const phuTrachRaw = (data.phuTrach ?? data.PhuTrach ?? []) as Record<string, unknown>[];
  const duocGiaoRaw = (data.duocGiao ?? data.DuocGiao ?? []) as Record<string, unknown>[];

  const phuTrach = phuTrachRaw
    .map(normalizeLoadMucTieuItem)
    .filter((m): m is { id: string; name: string; taskCount: number; rawName: string } => m != null);
  const duocGiao = duocGiaoRaw
    .map(normalizeLoadMucTieuItem)
    .filter((m): m is { id: string; name: string; taskCount: number; rawName: string } => m != null);

  const seen = new Set<string>();
  const all: { id: string; name: string; taskCount: number; rawName: string }[] = [];
  [...phuTrach, ...duocGiao].forEach((m) => {
    if (seen.has(m.id)) return;
    seen.add(m.id);
    all.push(m);
  });

  if (!all.length) {
    return { items: [], filterData: null };
  }

  const listMucTieuPhuTrachIDs = phuTrach.map((m) => m.id);

  return {
    items: all.map((m) => ({
      id: m.id,
      name: m.name,
      taskCount: m.taskCount,
    })),
    filterData: {
      listTableBSCNEW: [
        {
          phuongdienID: 'apv2-loadmuctieu',
          phuongdienName: '',
          listMucTieu: all.map((m) => ({
            muctieuID: m.id,
            // Giữ "Tên (n)" để TargetSelect / extractCountFromName đọc được
            muctieuName:
              m.taskCount > 0
                ? `${m.name} (${m.taskCount})`
                : m.rawName,
          })),
        } as Buoc6PhuongDien,
      ],
      listMucTieuPhuTrachIDs,
      listDuAn: [],
    },
  };
}

/** Map phuTrach/duocGiao từ API → filter BSC TargetSelect. */
export function mapMucTieuFilterFromApiPayload(
  data: Record<string, unknown> | null | undefined
): MucTieuFilterData | null {
  if (!data) return null;
  return mapLoadMucTieuV2Response(data).filterData;
}

// #endregion filterData

// #region mucTieuFilter

export const normalizeMucTieuFilterKey = (mtctid?: string | null): string => {
  const id = String(mtctid || '').trim();
  if (!id || id === '_none') return '_none';
  return id;
};

export const isValidMucTieuId = (mtctid?: string | null) => {
  const id = normalizeMucTieuFilterKey(mtctid);
  return id !== '_none' && id !== '0' && id !== '-1';
};

/** Parse chuỗi id mục tiêu (multi-select, cách nhau bởi dấu phẩy). Bỏ sentinel 0 / -1. */
export const parseMucTieuFilterIds = (raw: string) =>
  new Set(
    String(raw || '')
      .split(',')
      .map((id) => id.trim())
      .filter((id) => isValidMucTieuId(id))
  );

/** Giá trị lọc «không chọn mục tiêu nào» (bỏ chọn hết trong popover) — khác '' (= tất cả). */
export const MUC_TIEU_FILTER_NONE = '-1';

export const isNoMucTieuSelected = (raw?: string | null) => String(raw ?? '').trim() === MUC_TIEU_FILTER_NONE;

/** Chuẩn hóa giá trị Form / storage: '' = tất cả, '-1' = không chọn mục tiêu nào, còn lại = danh sách id. */
export const normalizeMucTieuFilterId = (raw?: string | null): string =>
  isNoMucTieuSelected(raw) ? MUC_TIEU_FILTER_NONE : [...parseMucTieuFilterIds(raw || '')].join(',');

/** Gửi lên API Employee Home — rỗng = không lọc (load đủ MT có việc); '-1' = không MT nào (BE trả rỗng). */
export const toIdMucTieuRequestParam = (raw?: string | null): string | undefined => {
  const v = normalizeMucTieuFilterId(raw || '');
  return v || undefined;
};

const mucTieuIdsEqual = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;

/**
 * Lọc client (Manager Home): rỗng = tất cả MT; có id = inclusive multi-select.
 * Employee Home: section tab đã lọc BE qua `IDMucTieu` — chỉ dùng helper này khi cần lọc local.
 */
export const matchesMucTieuFilter = (mtctid: string, mucTieuFilterId: string) => {
  if (isNoMucTieuSelected(mucTieuFilterId)) return false;
  const filterIds = parseMucTieuFilterIds(mucTieuFilterId);
  if (!filterIds.size) return true;
  const key = normalizeMucTieuFilterKey(mtctid);
  for (const id of filterIds) {
    if (mucTieuIdsEqual(id, key)) return true;
  }
  return false;
};

/** Id mục tiêu khi bộ lọc chỉ chọn đúng 1; nhiều hoặc không chọn → undefined. */
export const getSingleMucTieuFilterId = (raw?: string | null): string | undefined => {
  const ids = [...parseMucTieuFilterIds(raw || '')];
  return ids.length === 1 ? ids[0] : undefined;
};

// #endregion mucTieuFilter

// #region navigation

export function buildActionPlanV2MucTieuUrl(params: {
  mtctid?: string;
  /** CSV id mục tiêu đã chọn (TargetSelect) */
  targets?: string;
  /** Bộ phận đang chọn trên header — đồng bộ cây khi mở tab mới */
  idBoPhan?: string;
  /** Giá trị node cây header (vd. emp_{nvId}_dep_{bpId}) — ưu tiên khi sync nhân viên */
  treeSelectValue?: string;
  filter?: string;
  dateChoice?: string;
  /** '1' | '2' | '3' */
  tab?: string;
  /** 'mt' = bảng nhóm theo mục tiêu; bỏ trống = theo lựa chọn đã nhớ (mặc định không nhóm). */
  nhom?: 'mt';
}): string {
  const q = new URLSearchParams();
  if (params.targets) q.set('targets', params.targets);
  if (params.idBoPhan) q.set('idBoPhan', params.idBoPhan);
  if (params.treeSelectValue) q.set('treeSelectValue', params.treeSelectValue);
  if (params.filter) q.set('filter', params.filter);
  if (params.dateChoice) q.set('dateChoice', params.dateChoice);
  if (params.tab && params.tab !== '1') q.set('tab', params.tab);
  if (params.nhom) q.set('nhom', params.nhom);
  const query = q.toString();
  const pathId = params.mtctid || '0';
  return `${ACTION_PLAN_V2.ROUTE_MUC_TIEU}/${pathId}${query ? `?${query}` : ''}`;
}
type OrgEmployeeLike = {
  ID?: string;
  id?: string;
  BPID?: string;
  bpid?: string;
  IsQuanLy?: boolean;
  isQuanLy?: boolean;
  quanLy?: boolean;
};

/**
 * TreeSelect khi mở workspace theo người phụ trách:
 * - Quản lý / trưởng phòng → BPID
 * - Nhân viên → idBoPhan = ID NV, treeSelectValue = emp_{nv}_dep_{bp}
 */
export function resolveAssigneeTreeSelectParams(params: {
  /** nguoiPhuTrachID hoặc employeeId */
  assigneeId?: string | null;
  loadEmployee: (id: string) => OrgEmployeeLike | null | undefined;
  fallback?: { idBoPhan?: string; treeSelectValue?: string };
}): { idBoPhan?: string; treeSelectValue?: string } {
  const rawAssigneeId = String(params.assigneeId ?? '')
    .split(',')[0]
    .trim();
  if (!rawAssigneeId) {
    return params.fallback || {};
  }

  const employee = params.loadEmployee(rawAssigneeId);
  if (!employee) {
    return { idBoPhan: rawAssigneeId, treeSelectValue: rawAssigneeId };
  }

  const empId = String(employee.ID ?? employee.id ?? rawAssigneeId).trim();
  const bpId = String(employee.BPID ?? employee.bpid ?? '').trim();
  const isManager =
    (employee.IsQuanLy ?? employee.isQuanLy ?? employee.quanLy) === true;

  if (isManager) {
    if (bpId) {
      return { idBoPhan: bpId, treeSelectValue: bpId };
    }
  }

  return {
    idBoPhan: empId,
    treeSelectValue: bpId ? `emp_${empId}_dep_${bpId}` : empId,
  };
}

// #endregion navigation

// #region mucTieuOptions

export type MucTieuListRef = {
  muctieuID?: string;
  muctieuName?: string;
  /** Số công việc parse từ "Tên (n)" của loadmuctieu. */
  soCongViec?: number;
};

export function buildMucTieuNameByIdFromList(
  list?: MucTieuListRef[]
): Record<string, string> {
  const out: Record<string, string> = {};
  const items = Array.isArray(list) ? list : [];
  for (const item of items) {
    const id = String(item?.muctieuID ?? '').trim();
    if (!id) continue;
    const name = String(item?.muctieuName ?? '').trim();
    // KHÔNG ghi id-as-name: nếu không có tên thì bỏ qua để render fallback
    // qua record.congViec.tenCongViec (đã patch từ map tên đầy đủ) thay vì hiển thị id.
    if (name && name !== id) out[id] = name;
  }
  return out;
}

export function buildMucTieuTaskCountByIdFromList(
  list?: MucTieuListRef[]
): Record<string, number> {
  const out: Record<string, number> = {};
  const items = Array.isArray(list) ? list : [];
  for (const item of items) {
    const id = String(item?.muctieuID ?? '').trim();
    if (!id) continue;
    const n = Number(item?.soCongViec);
    out[id] = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  }
  return out;
}

export function buildMucTieuNameByIdFromTargetSelectGroups(
  phuTrach: TargetSelectPerspectiveGroup[] = [],
  khongPhuTrach: TargetSelectPerspectiveGroup[] = []
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const group of [...phuTrach, ...khongPhuTrach]) {
    for (const objective of group.objectives ?? []) {
      const id = String(objective.id ?? '').trim();
      if (!id) continue;
      out[id] = String(objective.name ?? id).trim() || id;
    }
  }
  return out;
}

export async function fetchMucTieuOptionsForPhuTrach(
  idChoice: string,
  year: number,
  phanHe = 'bsc'
): Promise<{ value: string; label: string }[]> {
  if (!idChoice) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res: any = await actionPlanV2Api.loadMucTieu({ idChoice, year, phanHe });
  const list: Record<string, unknown>[] = res?.data?.phuTrach ?? res?.data?.PhuTrach ?? [];
  return list
    .map((m) => {
      const id = String(m.id ?? m.ID ?? m.muctieuID ?? m.MucTieuID ?? m.mtctid ?? '').trim();
      if (!id) return null;
      const label = String(
        m.ten ?? m.Ten ?? m.muctieuName ?? m.MucTieuName ?? m.text ?? m.Text ?? id,
      ).trim();
      return { value: id, label: label || id };
    })
    .filter((m): m is { value: string; label: string } => m != null);
}

export async function fetchMucTieuOptionsForDepartment(
  departmentId: string,
  year: number,
): Promise<{ value: string; label: string }[]> {
  if (!departmentId) return [];
  // RequestSDK has required legacy fields; this endpoint only reads IDPhuTrach and Year.
  const response = await buoc6Api.getAccessView({ IDPhuTrach: departmentId, Year: year } as RequestSDK);
  if (!response || typeof response !== 'object') throw new Error('Không tải được danh sách mục tiêu theo phòng ban');
  const responseRecord = response as unknown as Record<string, unknown>;
  if (responseRecord.success === false) {
    throw new Error(String(responseRecord.message ?? 'Không tải được danh sách mục tiêu theo phòng ban'));
  }
  const accessView = responseRecord.data ?? responseRecord.Data ?? response;
  if (!accessView || typeof accessView !== 'object') {
    throw new Error('Không tải được danh sách mục tiêu theo phòng ban');
  }
  const accessViewRecord = accessView as Record<string, unknown>;
  const list = accessViewRecord.ListMucTieus ?? accessViewRecord.listMucTieus;
  if (!Array.isArray(list)) throw new Error('Không tải được danh sách mục tiêu theo phòng ban');
  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const objective = item as Record<string, unknown>;
      const id = String(
        objective.ID ?? objective.id ?? objective.muctieuID ?? objective.MucTieuID ?? '',
      ).trim();
      if (!id) return null;
      const label = String(
        objective.Ten ?? objective.ten ?? objective.muctieuName ?? objective.MucTieuName ?? id,
      ).trim();
      return { value: id, label: label || id };
    })
    .filter((item): item is { value: string; label: string } => item != null);
}

// #endregion mucTieuOptions
